import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import type {
  OrganizationStatsRaw,
  UserSummaryStats,
} from '../../../types/employeeAnalysis'
import {
  buildFYOptions,
  getCurrentFYStartYear,
  type PeriodFilter,
} from '../../../utils/projectAnalysisPeriod'
import {
  computeOrgKpisFromRaw,
  computeStatusFilteredOrgKpis,
  resolveOrgEmployeeDisplayKpis,
} from '../../../utils/computeEmployeeOrgKpis'
import { buildUserWiseRowsFromSummary } from '../../../utils/employeeUserWiseList'
import { useEmployeeGoldFact } from '../../../hooks/use-employee-gold-fact'
import { useEmployeeDetailPage } from '../../../hooks/use-employee-detail-page'
import { AppLayout } from '../../layouts/AppLayout'
import { PageHeader } from '../../layouts/PageHeader'
import { useTheme } from '../../../hooks/useTheme'
import { EMPLOYEE_ANALYSIS_PAGE_TITLE } from './constants'
import {
  EmployeeOrgFilters,
  type OrgFilterMode,
} from './employee-org-filters'
import { EmployeeOrgKpiStrip } from './employee-org-kpi-strip'
import { EmployeeTopNSection } from './employee-top-n-section'
import { EmployeeUserWiseTable } from './employee-user-wise-table'
import { ProjectDetailPeriodControls } from '../ProjectAnalysis/project-detail-period-controls'
import { EmployeeDetailHeaderBar } from './employee-detail-header-bar'
import { EmployeeDetailTabBar } from './employee-detail-tab-bar'
import { EmployeeDetailTabContent } from './employee-detail-tab-content'
import {
  EmployeeDetailEmptyPeriod,
  EmployeeDetailFetchError,
  EmployeeDetailLoading,
  EmployeeDetailNoData,
} from './employee-detail-states'
import { appPath } from '../../../utils/appPath'

export const EmployeeAnalysisPage: React.FC = () => {
  const { mounted } = useTheme()
  const navigate = useNavigate()
  const { userId: userIdParam } = useParams<{ userId?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const numericId = userIdParam ? Number(userIdParam) : NaN
  const selectedUserId =
    Number.isFinite(numericId) && numericId > 0 ? numericId : null

  const appliedQueryUserId = useRef(false)
  useEffect(() => {
    if (appliedQueryUserId.current) return
    const q = searchParams.get('userId')?.trim()
    if (!q) return
    const n = Number(q)
    if (!Number.isFinite(n) || n <= 0) return
    appliedQueryUserId.current = true
    navigate(appPath(`/employee-analysis/${n}`), { replace: true })
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('userId')
        return next
      },
      { replace: true },
    )
  }, [searchParams, navigate, setSearchParams])

  const [orgRaw, setOrgRaw] = useState<OrganizationStatsRaw | null>(null)
  const [userSummary, setUserSummary] = useState<UserSummaryStats | null>(null)
  const [listLoading, setListLoading] = useState(true)

  const [orgFilterMode, setOrgFilterMode] = useState<OrgFilterMode>('fy')
  const [orgYear, setOrgYear] = useState('all')
  const [orgMonth, setOrgMonth] = useState('all')
  const [orgFY, setOrgFY] = useState('2025-26')
  const [orgQ, setOrgQ] = useState('all')
  const [orgStatusFilter, setOrgStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all')

  const orgFilter: PeriodFilter = useMemo(() => {
    if (orgFilterMode === 'fy')
      return { year: 'all', month: 'all', fy: orgFY, q: orgQ }
    return { year: orgYear, month: orgMonth, fy: 'all', q: 'all' }
  }, [orgFilterMode, orgYear, orgMonth, orgFY, orgQ])

  const orgYearOptions = useMemo(() => {
    if (!orgRaw) return []
    const y = orgRaw.yearly_breakdown
      ? Object.keys(orgRaw.yearly_breakdown).map(Number)
      : []
    const fromTrends = (orgRaw.monthly_trends || []).map((m) => m.year)
    const combined = [...new Set([...y, ...fromTrends])]
      .filter(Boolean)
      .sort((a, b) => b - a)
    return combined
  }, [orgRaw])

  const orgFYOptions = useMemo(() => {
    const opts = buildFYOptions(orgYearOptions, getCurrentFYStartYear())
    return opts.length ? opts : [{ value: '2025-26', label: 'FY 2025-26' }]
  }, [orgYearOptions])

  useEffect(() => {
    if (orgFilterMode !== 'fy' || orgFYOptions.length === 0) return
    const valid = orgFYOptions.some((o) => o.value === orgFY)
    if (!valid) setOrgFY(orgFYOptions[0].value)
  }, [orgFilterMode, orgFY, orgFYOptions])

  useEffect(() => {
    if (selectedUserId) return
    let cancelled = false
    setListLoading(true)
    Promise.all([
      fetch('/ts_user_jsons/organization_stats.json').then((r) =>
        r.ok ? r.json() : null,
      ),
      fetch('/ts_user_jsons/user_summary_stats.json').then((r) =>
        r.ok ? r.json() : null,
      ),
    ])
      .then(([org, sum]) => {
        if (cancelled) return
        if (org) setOrgRaw(org as OrganizationStatsRaw)
        if (sum) setUserSummary(sum as UserSummaryStats)
      })
      .finally(() => {
        if (!cancelled) setListLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedUserId])

  const displayKpis = useMemo(() => {
    if (!orgRaw) return null
    const base = computeOrgKpisFromRaw(orgRaw, orgFilter)
    let statusFiltered = null
    if (
      userSummary &&
      (orgStatusFilter === 'active' || orgStatusFilter === 'inactive')
    ) {
      statusFiltered = computeStatusFilteredOrgKpis(
        userSummary,
        orgFilter,
        orgStatusFilter,
      )
    }
    return resolveOrgEmployeeDisplayKpis(
      base,
      statusFiltered,
      orgStatusFilter,
    )
  }, [orgRaw, orgFilter, userSummary, orgStatusFilter])

  const tableBaseRows = useMemo(() => {
    const rows = buildUserWiseRowsFromSummary(userSummary, orgFilter)
    if (orgStatusFilter === 'active')
      return rows.filter((u) => u.is_active)
    if (orgStatusFilter === 'inactive')
      return rows.filter((u) => !u.is_active)
    return rows
  }, [userSummary, orgFilter, orgStatusFilter])

  const { data: goldData, loading: goldLoading, error: goldError } =
    useEmployeeGoldFact(selectedUserId)

  const detailModel = useEmployeeDetailPage({
    userId: selectedUserId ?? 0,
    data: selectedUserId ? goldData : [],
    loading: Boolean(selectedUserId && goldLoading),
    error: selectedUserId ? goldError : null,
    onBack: () => navigate(appPath('/employee-analysis')),
  })

  if (!mounted) return null

  const detailPeriodSlot = selectedUserId ? (
    <ProjectDetailPeriodControls
      detailMode={detailModel.detailMode}
      onDetailModeChange={detailModel.setDetailMode}
      detailFY={detailModel.detailFY}
      onDetailFYChange={detailModel.setDetailFY}
      detailQ={detailModel.detailQ}
      onDetailQChange={detailModel.setDetailQ}
      detailYear={detailModel.detailYear}
      onDetailYearChange={detailModel.setDetailYear}
      detailMonth={detailModel.detailMonth}
      onDetailMonthChange={detailModel.setDetailMonth}
      detailFYOptions={detailModel.profileFYOptions}
      detailYearOptions={detailModel.profileYearOptions}
      className="w-full lg:w-auto justify-end"
    />
  ) : null

  return (
    <AppLayout>
      {!selectedUserId ? (
        <>
          <PageHeader
            title={EMPLOYEE_ANALYSIS_PAGE_TITLE}
            filters={
              <EmployeeOrgFilters
                orgFilterMode={orgFilterMode}
                onOrgFilterModeChange={(m) => {
                  setOrgFilterMode(m)
                  if (m === 'fy') {
                    setOrgYear('all')
                    setOrgMonth('all')
                    setOrgFY((prev) =>
                      orgFYOptions.some((o) => o.value === prev)
                        ? prev
                        : orgFYOptions[0]?.value || '2025-26',
                    )
                  } else {
                    setOrgFY('all')
                    setOrgQ('all')
                  }
                }}
                orgFY={orgFY}
                onOrgFYChange={setOrgFY}
                orgQ={orgQ}
                onOrgQChange={setOrgQ}
                orgYear={orgYear}
                onOrgYearChange={setOrgYear}
                orgMonth={orgMonth}
                onOrgMonthChange={setOrgMonth}
                orgFYOptions={orgFYOptions}
                orgYearOptions={orgYearOptions}
                orgStatusFilter={orgStatusFilter}
                onOrgStatusFilterChange={setOrgStatusFilter}
              />
            }
          />
          <div className="p-6 max-w-[1600px] space-y-6 pb-12">
            <EmployeeOrgKpiStrip
              display={displayKpis}
              loading={listLoading}
            />
            <EmployeeTopNSection
              periodFilter={orgFilter}
              statusFilter={orgStatusFilter}
              onUserSelect={(id) => navigate(appPath(`/employee-analysis/${id}`))}
            />
            <EmployeeUserWiseTable
              baseRows={tableBaseRows}
              loading={listLoading}
              periodFilter={orgFilter}
              onRowNavigate={(id) =>
                navigate(appPath(`/employee-analysis/${id}`))
              }
            />
          </div>
        </>
      ) : goldLoading ? (
        <EmployeeDetailLoading userId={selectedUserId} />
      ) : goldError ? (
        <EmployeeDetailFetchError message={goldError} />
      ) : goldData.length === 0 ? (
        <EmployeeDetailNoData m={detailModel} />
      ) : !detailModel.userStats ? (
        <EmployeeDetailEmptyPeriod m={detailModel} />
      ) : (
        <>
          <PageHeader>
            <EmployeeDetailHeaderBar
              title={detailModel.headerTitle}
              statusLabel={
                detailModel.headerStatusActive ? 'Active' : 'Inactive'
              }
              email={detailModel.headerEmail}
              username={detailModel.headerUsername}
              userId={detailModel.userId}
              periodSlot={detailPeriodSlot}
            />
          </PageHeader>
          <div className="p-6">
            <div className="space-y-3">
              <EmployeeDetailTabBar
                activeTab={detailModel.activeTab}
                onTabChange={detailModel.setActiveTab}
              />
              <EmployeeDetailTabContent m={detailModel} />
            </div>
          </div>
        </>
      )}
    </AppLayout>
  )
}

export function EmployeeAnalysisRedirectFromTimesheet() {
  return <Navigate to={appPath('/employee-analysis')} replace />
}

export default EmployeeAnalysisPage
